import { Controller } from '@hotwired/stimulus';
import fitAutoresizeTextareas from 'helpers/autoresize';
import { fillRegion, isPlainKey, nameSegments, placeInOrder } from 'helpers/form-region';
import recheckRangePairs from 'helpers/goals-v3-range-pairs';

// Units written straight after the number, with no space ("90%", "150°").
const ATTACHED_UNITS = ['%', '°'];

// A target typed into two boxes, in a wrapper marked data-goals-v3-capture-proposed-target:
// "range" is a Range of motion's start and end, "trials" a counting goal's correct and total.
const isRangeTarget = (target) => target.dataset.goalsV3CaptureProposedTarget === 'range';
const isTrialsTarget = (target) => target.dataset.goalsV3CaptureProposedTarget === 'trials';
const isPairTarget = (target) => isRangeTarget(target) || isTrialsTarget(target);

// What a typed target reads as, or '' for none. An Impulse select keeps the
// chosen grade's words on its hidden value input; a range reads the way
// therapists write it ("0-150°", "?" for a box still empty), and a counting
// goal's pair as trials ("8/10"); a number wears the goal's unit, spaced the
// way the saved chip writes it ("12 per session"), with the symbols that
// attach joined ("90%", "150°").
const targetWords = (target) => {
  const unit = target.dataset.unit || '';
  if (isPairTarget(target)) {
    const [first, second] = Array.from(target.querySelectorAll('input')).map((box) => box.value.trim());
    if (!first && !second) return '';
    if (isTrialsTarget(target)) return `${first || '?'}/${second || '?'}`;
    return `${first || '?'}-${second || '?'}${unit}`;
  }
  if (target.value === '') return '';
  const carrier = target.querySelector('input[data-behavior="hidden-field"]');
  const chosen = (carrier && carrier.dataset.text) || target.value;
  const spacer = unit && !ATTACHED_UNITS.includes(unit) ? ' ' : '';
  return `${chosen}${spacer}${unit}`;
};

// Stimulus controller for the Goals v3 session-data capture control on visit
// notes (smart-goals-v3-data-collection).
//
// Responsibilities:
//   - Add / remove data-point entry lines per goal (template clone + DOM
//     insertion, mirroring the goals editor's key idiom).
//   - Keep the trials cluster's live accuracy badge honest ("= 80%").
//   - The session-wide label-row controls: "+ Add goal" (modal of unlanded
//     goals), "Toggle goals" (open/collapse every landed row — visual only,
//     collapsed inputs still submit), "Clear goals" (unland everything
//     except goals whose datum already saved entries). Toggle goals, Clear
//     goals and Customize Goals Shown show only while a goal is landed.
//   - Asking before a goal leaves the session holding something entered for
//     it (Clear goals, the row's x, the sidebar checkbox).
//   - Putting the section back from a copy auto-save kept (restore).
//   - Per-row collapse via the header/caret, like the editor's rows.
//
// Removal is DOM-only: the SessionDataReconciler rebuilds the note's v3
// measurement rows from the submitted datum on every unsigned save, so an
// entry absent from the form is a data point removed from the note.
//
// The sidebar's Current-goals picker decides which goal rows are LANDED:
// unlanded rows are hidden AND disabled (they submit nothing), and the two
// columns talk over window events (see goals_v3_goal_picker_controller.js
// for the event vocabulary).
export default class extends Controller {
  static targets = [
    'goalRow',
    'entries',
    'emptyHint',
    'disclosure',
    'addButton',
    'toggleButton',
    'clearButton',
    'presentationButton',
    'panelMasters',
    'addOption',
    'addGroup',
    'addAllLanded',
    'addAll',
    'targetLink',
    'targetPanel',
    'proposalChip',
    'proposalLabel',
    'removeModal',
    'removeTitle',
    'removeMessage',
    'removeConfirm',
  ];

  static values = { controlId: String };

  connect() {
    this._watchModalTransitions();
    // Unchecking a goal in the sidebar takes it out the way the row's x does.
    this._onSet = (event) => {
      const { goalId, landed } = event.detail || {};
      if (landed) {
        this.setLanded(goalId, true);
      } else {
        this._requestRemoval([goalId], { clearing: false });
      }
    };
    this._onJump = (event) => {
      const row = this._rowFor(event.detail && event.detail.goalId);
      if (row && !row.hidden) {
        this._reveal(row);
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this._flash(row);
      }
    };
    // The sidebar's Select all: every goal into the session, or every goal
    // out, the way Clear goals takes them (asking first when one holds
    // something, keeping the ones with saved entries).
    this._onSetAll = (event) => this.setAllLanded(!!(event.detail && event.detail.landed));
    this._onRequestState = () => this.broadcastState();
    // Values typed into entry lines change the picker's "N have data"
    // summary; input catches the native fields, the commit event the
    // Impulse selects. A person typing into a line the AI wrote makes it
    // theirs: its source and confidence clear and its mark goes (D22).
    this._onEntryInput = (event) => {
      this._claimLine(event.target);
      this.broadcastState();
    };
    // A tucked-away row hides its inputs (display:none), and so does a goal
    // hidden with Customize Goals Shown; the browser can neither focus nor
    // report a hidden invalid field, so the save would fail without a word.
    // The row holding the field the browser flags shows and opens first. The
    // choice the note saves is left as it was: the row only shows so its
    // message can.
    this._onInvalid = (event) => {
      const row = event.target.closest && event.target.closest('[data-goal-key]');
      if (!row) return;
      row.classList.remove('gv3-presentation-hidden');
      this._setRowOpen(row, true);
    };
    window.addEventListener('goals-v3-capture:set', this._onSet);
    window.addEventListener('goals-v3-capture:set-all', this._onSetAll);
    window.addEventListener('goals-v3-capture:jump', this._onJump);
    window.addEventListener('goals-v3-capture:request-state', this._onRequestState);
    this.element.addEventListener('input', this._onEntryInput);
    this.element.addEventListener('awc-autocomplete:commit', this._onEntryInput);
    this.element.addEventListener('invalid', this._onInvalid, true);
    // A reopened note's saved lines read their accuracy right away.
    this.element.querySelectorAll('[data-goals-v3-capture-entry]').forEach((entry) => this._refreshAccuracy(entry));
    this._syncSessionControls();
    this.broadcastState();
  }

  disconnect() {
    window.removeEventListener('goals-v3-capture:set', this._onSet);
    window.removeEventListener('goals-v3-capture:set-all', this._onSetAll);
    window.removeEventListener('goals-v3-capture:jump', this._onJump);
    window.removeEventListener('goals-v3-capture:request-state', this._onRequestState);
    this.element.removeEventListener('input', this._onEntryInput);
    this.element.removeEventListener('awc-autocomplete:commit', this._onEntryInput);
    this.element.removeEventListener('invalid', this._onInvalid, true);
  }

  // Lands or unlands a goal row: visibility and enablement move together, so
  // an unlanded row submits nothing and the reconciler's rebuild drops its
  // rows — "unchecking takes it back out while nothing is saved against it".
  // A goal whose datum already holds saved entries is locked into the
  // session (data-saved) and never unlands from here.
  setLanded(goalId, landed) {
    const row = this._rowFor(goalId);
    if (!row) return;
    if (!landed && row.dataset.saved === 'true') return;

    row.hidden = !landed;
    row.dataset.landed = String(landed);
    row.querySelectorAll('input, select, textarea, button').forEach((el) => {
      el.disabled = !landed;
    });
    // Impulse selects (the prompting pick, grade ladders, binary words)
    // drive themselves off the <awc-autocomplete> element's own disabled
    // attribute — enabling only their inner inputs leaves the dropdown
    // dead, so the component itself joins the toggle (same as the goals
    // editor's region lock).
    row.querySelectorAll('awc-autocomplete').forEach((el) => {
      if (landed) {
        el.removeAttribute('disabled');
      } else {
        el.setAttribute('disabled', '');
      }
    });
    if (landed) {
      this._reveal(row);
      this._setRowOpen(row, true);
    }
    // Positions follow what the therapist sees: a goal added after a reorder
    // saves where it shows, not after every positioned goal.
    this._renumberPositions();
    this._syncEmptyHint();
    this._syncSessionControls();
    this.broadcastState();
  }

  // Row header click (including the caret button's native activation):
  // tuck the row's entry band away or bring it back. Visual only — the
  // collapsed inputs stay enabled and submit with the note. The grip, the
  // status select, and the remove strip are their own controls and never
  // toggle the row.
  toggleRow(event) {
    if (event.target.closest('.gv3-grip, .gv3-row-remove-strip, select, a, input, textarea, awc-autocomplete')) return;
    const header = event.currentTarget;
    const row = header.closest('[data-goal-key]');
    if (!row) return;
    const detail = row.querySelector('.goals-v3-goal-collapsible');
    if (!detail) return;
    this._setRowOpen(row, detail.classList.contains('hidden'));
  }

  // --- Proposals (the status slot + Adjust target) ----------------------
  // One proposal at a time, the prototype rule: picking a closing status
  // clears a staged target change and vice versa. The chip narrates what is
  // staged; withdraw resets everything to "keep".
  //
  // The status slot and a graded target are Impulse selects: an
  // <awc-autocomplete> whose value is read-only (clear() empties it without
  // announcing a change) and which announces a pick with
  // awc-autocomplete:commit and its clear button with awc-autocomplete:clear.
  // The status slot's blank value is keep. A Range of motion target is two
  // boxes, a start and an end, in a wrapper marked
  // data-goals-v3-capture-proposed-target="range".

  statusChanged(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    if (this._proposedStatus(row)) this._clearTarget(row);
    this._syncProposal(row);
  }

  openTargetPanel(event) {
    event.stopPropagation();
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    const panel = row.querySelector('[data-goals-v3-capture-target~="targetPanel"]');
    const link = row.querySelector('[data-goals-v3-capture-target~="targetLink"]');
    if (panel) panel.hidden = false;
    if (link) link.hidden = true;
    const input = row.querySelector('[data-goals-v3-capture-proposed-target]');
    const box = input && isPairTarget(input) ? input.querySelector('input') : input;
    if (box) box.focus();
  }

  closeTargetPanel(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    this._clearTarget(row);
    this._syncProposal(row);
  }

  targetChanged(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    if (event.currentTarget.value !== '') this._resetStatus(row);
    this._syncProposal(row);
  }

  withdrawProposal(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    this._resetStatus(row);
    this._clearTarget(row);
    this._syncProposal(row);
  }

  // The status the slot proposes ('met' / 'discontinued'), or '' for keep.
  _proposedStatus(row) {
    const status = row.querySelector('[data-goals-v3-capture-status]');
    return status ? status.value : '';
  }

  _resetStatus(row) {
    const status = row.querySelector('[data-goals-v3-capture-status]');
    if (status && status.value !== '') status.clear();
  }

  _clearTarget(row) {
    const input = row.querySelector('[data-goals-v3-capture-proposed-target]');
    // A graded target is an Impulse select (clear()); a range or a counting goal's pair is two
    // boxes, emptied with whatever they were flagged for; any other is a number input.
    if (input && typeof input.clear === 'function') input.clear();
    else if (input && isPairTarget(input)) {
      input.querySelectorAll('input').forEach((box) => {
        box.value = '';
        box.setCustomValidity('');
      });
    } else if (input) input.value = '';
    const panel = row.querySelector('[data-goals-v3-capture-target~="targetPanel"]');
    if (panel) panel.hidden = true;
    const link = row.querySelector('[data-goals-v3-capture-target~="targetLink"]');
    if (link) link.hidden = false;
  }

  _syncProposal(row) {
    const chip = row.querySelector('[data-goals-v3-capture-target~="proposalChip"]');
    const label = row.querySelector('[data-goals-v3-capture-target~="proposalLabel"]');
    if (!chip || !label) return;
    const parts = [];
    const status = this._proposedStatus(row);
    if (status) parts.push(`mark ${status}`);
    const target = row.querySelector('[data-goals-v3-capture-proposed-target]');
    const words = target ? targetWords(target) : '';
    if (words) parts.push(`target → ${words}`);
    chip.hidden = parts.length === 0;
    label.textContent = `Proposed: ${parts.join(' · ')}`;
  }

  // The header's right-edge x: take the goal back out of the session (the
  // same unland the sidebar checkbox performs; saved rows never render it).
  removeFromSession(event) {
    event.stopPropagation();
    const row = event.currentTarget.closest('[data-goal-key]');
    if (row) this._requestRemoval([row.dataset.goalKey], { clearing: false });
  }

  // --- Taking goals out of the session ----------------------------------
  // A goal leaves the session from its row's x, from Clear goals, or when
  // its sidebar checkbox is unchecked, and what was entered for it is then
  // left out of the note when it saves. A goal holding nothing leaves at
  // once; otherwise the modal names the goals holding something and asks
  // first. A goal with saved entries never leaves (setLanded refuses it).

  _requestRemoval(goalIds, { clearing }) {
    const rows = goalIds
      .map((goalId) => this._rowFor(goalId))
      .filter((row) => row && row.dataset.landed === 'true' && row.dataset.saved !== 'true');
    if (rows.length === 0) return;

    const holding = rows.filter((row) => this._rowHoldsEntries(row));
    if (holding.length === 0 || !this.hasRemoveModalTarget) {
      rows.forEach((row) => this.setLanded(row.dataset.goalKey, false));
      return;
    }

    this._pendingRemoval = rows.map((row) => row.dataset.goalKey);
    const labels = holding.map((row) => row.dataset.goalLabel);
    if (clearing) {
      this.removeTitleTarget.textContent = 'Clear goals from this session?';
      this.removeMessageTarget.textContent = `What you entered for ${this._listed(labels)} will not be saved with the note.`;
      this.removeConfirmTarget.textContent = 'Clear goals';
    } else {
      this.removeTitleTarget.textContent = `Remove ${labels[0]} from this session?`;
      this.removeMessageTarget.textContent = 'What you entered for this goal will not be saved with the note.';
      this.removeConfirmTarget.textContent = 'Remove goal';
    }
    this._showRemoveModal();
  }

  confirmRemoval() {
    const goalIds = this._pendingRemoval || [];
    this._pendingRemoval = null;
    this._hideRemoveModal();
    goalIds.forEach((goalId) => this.setLanded(goalId, false));
  }

  cancelRemoval() {
    this._hideRemoveModal();
    this._removalDismissed();
  }

  // Closing the modal any other way (Cancel, Escape, the backdrop) keeps the
  // goals, and puts back the sidebar checkbox an uncheck cleared.
  _removalDismissed() {
    if (!this._pendingRemoval) return;
    this._pendingRemoval = null;
    this.broadcastState();
  }

  _showRemoveModal() {
    const modal = this.removeModalTarget;
    const $ = this._bootstrapModal();
    if ($) {
      $(modal).one('hidden.bs.modal', () => this._removalDismissed());
      $(modal).modal('show');
    } else {
      modal.classList.add('show');
      modal.style.display = 'block';
    }
  }

  _hideRemoveModal() {
    this._closeModal(this.removeModalTarget);
  }

  // jQuery, when Bootstrap's modal plugin is on the page.
  _bootstrapModal() {
    const $ = typeof window !== 'undefined' && typeof window.$ === 'function' ? window.$ : null;
    return $ && $.fn && $.fn.modal ? $ : null;
  }

  // Bootstrap's Modal.hide() returns early while the modal is still fading in, so a pick made
  // before the fade ends (a fast keyboard user, a test) would leave the modal open over the row.
  // Each of this control's modals wears data-transitioning from show until shown, and data-shown
  // from shown until hidden; _closeModal waits for shown when it must.
  _watchModalTransitions() {
    const $ = this._bootstrapModal();
    if (!$) return;
    this.element.querySelectorAll('.modal').forEach((modal) => {
      $(modal).on('show.bs.modal', () => {
        modal.dataset.transitioning = 'true';
      });
      $(modal).on('shown.bs.modal', () => {
        delete modal.dataset.transitioning;
        modal.dataset.shown = 'true';
      });
      $(modal).on('hidden.bs.modal', () => {
        delete modal.dataset.shown;
      });
    });
  }

  // Whether taking the goal out would leave something out of the note: a
  // data point's value or prompting, the session comment, or a proposed
  // status or target. The trials total starts filled in, and the landed and
  // position markers are the row's own, so none of those counts.
  _rowHoldsEntries(row) {
    return Array.from(row.querySelectorAll('input[name], textarea[name]')).some((field) => {
      if (/\[(landed|position|total)\]$/.test(field.name)) return false;
      return field.value != null && String(field.value).trim() !== '';
    });
  }

  // "LTG 1.0.1", "LTG 1.0.1 and STG 1.1.0", "LTG 1.0.1, STG 1.1.0 and LTG 2.0.1".
  _listed(labels) {
    if (labels.length < 2) return labels.join('');
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  // --- Restoring a copy auto-save kept ------------------------------------
  // auto-save:restore hands back a copy of this section kept by auto-save,
  // the session-timeout save or the offline save: what it would have
  // submitted then (helpers/form-region.js). The goals the copy has landed
  // are the session again, in the copy's order, each with exactly the copy's
  // data lines, values, comment and proposals. Every other goal leaves the
  // session, except one with saved entries, which never does.

  restore(event) {
    const fields = (event.detail && event.detail.fields) || [];
    const prefix = this.element.dataset.autoSaveRegion;
    const copied = new Map();
    fields.forEach(([name]) => {
      const [goalId, part, entryKey] = nameSegments(name, prefix);
      if (!goalId) return;
      if (!copied.has(goalId)) copied.set(goalId, { landed: false, entryKeys: [] });
      const goal = copied.get(goalId);
      if (part === 'landed') goal.landed = true;
      if (part === 'entries' && entryKey && !goal.entryKeys.includes(entryKey)) goal.entryKeys.push(entryKey);
    });
    const landedInCopy = (goalId) => copied.has(goalId) && copied.get(goalId).landed;

    this.goalRowTargets.forEach((row) => this.setLanded(row.dataset.goalKey, landedInCopy(row.dataset.goalKey)));

    const rows = Array.from(copied.keys())
      .filter(landedInCopy)
      .map((goalId) => this._rowFor(goalId))
      .filter(Boolean);
    placeInOrder(rows);
    rows.forEach((row) => this._rebuildEntries(row, copied.get(row.dataset.goalKey).entryKeys));
    fillRegion(
      this.element,
      fields.filter(([name]) => landedInCopy(nameSegments(name, prefix)[0])),
      prefix
    );

    rows.forEach((row) => {
      row.querySelectorAll('[data-goals-v3-capture-entry]').forEach((entry) => {
        this._refreshAccuracy(entry);
        this._syncMark(entry);
      });
      recheckRangePairs(this.application, row);
      this._syncTargetPanel(row);
      this._syncProposal(row);
    });
    this._renumberPositions();
    this.broadcastState();
  }

  // The copy's data lines in place of the row's, each under the key it had.
  // A key the page could not have made never goes into the markup.
  _rebuildEntries(row, entryKeys) {
    const template = row.querySelector('template[data-goals-v3-capture-template]');
    const entries = row.querySelector('[data-goals-v3-capture-target="entries"]');
    if (!template || !entries) return;

    entries.querySelectorAll('[data-goals-v3-capture-entry]').forEach((entry) => entry.remove());
    entryKeys.filter(isPlainKey).forEach((entryKey) => {
      entries.insertAdjacentHTML('beforeend', template.innerHTML.replaceAll('__ENTRY__', entryKey));
    });
  }

  // The Adjust target panel shows while a target is staged, as the page
  // renders it (a Range of motion target once either of its boxes holds a
  // number).
  _syncTargetPanel(row) {
    const input = row.querySelector('[data-goals-v3-capture-proposed-target]');
    const panel = row.querySelector('[data-goals-v3-capture-target~="targetPanel"]');
    if (!input || !panel) return;
    const staged = targetWords(input) !== '';
    panel.hidden = !staged;
    const link = row.querySelector('[data-goals-v3-capture-target~="targetLink"]');
    if (link) link.hidden = staged;
  }

  // --- Reorder: grip-only drag origin + keyboard fallback ---------------
  // The grip arms the row's native draggability on mousedown (nothing else
  // starts a drag), the hovered row shows an insertion edge on the side the
  // goal will land, and the drop renumbers every landed row's position
  // input so the order persists in the datum.

  armDrag(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (row) row.draggable = true;
  }

  disarmDrag(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (row) row.draggable = false;
  }

  // Only the row the grip armed moves. A drag that starts inside the row (a
  // link such as the Details tab or a line's remove x, or selected text)
  // bubbles here too, and is left to the browser.
  dragStart(event) {
    const row = event.currentTarget;
    if (event.target !== row || !row.draggable) return;
    this._dragRow = row;
    row.classList.add('gv3-reorder-dragging');
    event.dataTransfer.effectAllowed = 'move';
    try {
      event.dataTransfer.setData('text/plain', row.dataset.goalKey);
    } catch {
      /* some engines require setData; failure is harmless */
    }
  }

  dragOver(event) {
    if (!this._dragRow) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const row = event.currentTarget;
    if (row === this._dragRow) {
      this._clearDropEdges();
      return;
    }
    const rect = row.getBoundingClientRect();
    const above = event.clientY < rect.top + rect.height / 2;
    row.classList.toggle('gv3-drop-above', above);
    row.classList.toggle('gv3-drop-below', !above);
  }

  dragLeave(event) {
    const row = event.currentTarget;
    if (!row.contains(event.relatedTarget)) row.classList.remove('gv3-drop-above', 'gv3-drop-below');
  }

  drop(event) {
    if (!this._dragRow) return;
    event.preventDefault();
    const target = event.currentTarget;
    const above = target.classList.contains('gv3-drop-above');
    const below = target.classList.contains('gv3-drop-below');
    this._clearDropEdges();
    if (target === this._dragRow || (!above && !below)) return;
    target.parentNode.insertBefore(this._dragRow, above ? target : target.nextSibling);
    this._renumberPositions();
  }

  dragEnd(event) {
    const row = event.currentTarget;
    row.classList.remove('gv3-reorder-dragging');
    row.draggable = false;
    this._dragRow = null;
    this._clearDropEdges();
  }

  // ArrowUp/ArrowDown on the focused grip: swap with the neighboring landed
  // row and keep the grip focused so the row can keep moving.
  gripKeydown(event) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const grip = event.currentTarget;
    const row = grip.closest('[data-goal-key]');
    if (!row) return;
    const landed = this._landedRows();
    const index = landed.indexOf(row);
    const target = landed[index + (event.key === 'ArrowUp' ? -1 : 1)];
    if (!target) return;
    target.parentNode.insertBefore(row, event.key === 'ArrowUp' ? target : target.nextSibling);
    this._renumberPositions();
    grip.focus();
  }

  // The label row's "Toggle goals": any landed row tucked away opens them
  // all; otherwise closes them all (the prototype's alternating master).
  toggleAllGoals() {
    const rows = this._landedRows();
    if (rows.length === 0) return;
    const opening = rows.some((row) => {
      const detail = row.querySelector('.goals-v3-goal-collapsible');
      return detail && detail.classList.contains('hidden');
    });
    rows.forEach((row) => this._setRowOpen(row, opening));
  }

  // The label row's "Clear goals": every goal leaves the session except the
  // ones the note already saved entries for (setLanded refuses those),
  // asking first when a goal leaving holds something.
  clearGoals() {
    this._requestRemoval(
      this._landedRows().map((row) => row.dataset.goalKey),
      { clearing: true }
    );
  }

  // Every goal into the session at once (the sidebar's Select all, the
  // modal's Add all), or every goal out, which is Clear goals. One state
  // broadcast at the end rather than one per goal.
  setAllLanded(landed) {
    if (!landed) {
      this.clearGoals();
      return;
    }
    const rows = this.goalRowTargets.filter((row) => row.dataset.landed !== 'true');
    if (rows.length === 0) return;
    this._broadcastPaused = true;
    try {
      rows.forEach((row) => this.setLanded(row.dataset.goalKey, true));
    } finally {
      this._broadcastPaused = false;
    }
    this.broadcastState();
  }

  // The modal's Add all: every goal not yet in the session lands, the modal
  // closes, and the page scrolls to the first of them.
  addAllFromModal(event) {
    const first = this.goalRowTargets.find((row) => row.dataset.landed !== 'true');
    this.setAllLanded(true);
    this._closeModal(event.currentTarget.closest('.modal'));
    if (first && !first.hidden) {
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this._flash(first);
    }
  }

  // A pick from the "+ Add goal" modal: land the goal, close the modal, and
  // scroll to the landed row.
  addGoalFromModal(event) {
    const goalId = event.params.key;
    if (!goalId) return;
    this.setLanded(goalId, true);
    this._closeModal(event.currentTarget.closest('.modal'));

    const row = this._rowFor(goalId);
    if (row && !row.hidden) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this._flash(row);
    }
  }

  _closeModal(modal) {
    if (!modal) return;
    const $ = this._bootstrapModal();
    if ($) {
      if (modal.dataset.transitioning === 'true') {
        // Still fading in: hide() would be a no-op, so hide once the fade ends.
        $(modal).one('shown.bs.modal', () => $(modal).modal('hide'));
      } else {
        $(modal).modal('hide');
      }
    } else {
      modal.classList.remove('show');
      modal.style.display = 'none';
    }
  }

  broadcastState() {
    if (this._broadcastPaused) return;
    const landed = [];
    const withData = [];
    const saved = [];
    this.goalRowTargets.forEach((row) => {
      if (row.dataset.saved === 'true') saved.push(row.dataset.goalKey);
      if (row.dataset.landed !== 'true') return;
      landed.push(row.dataset.goalKey);
      if (this._rowHasData(row)) withData.push(row.dataset.goalKey);
    });
    window.dispatchEvent(
      new CustomEvent('goals-v3-capture:state', {
        detail: { landed, withData, saved, total: this.goalRowTargets.length },
      })
    );
  }

  _rowFor(goalId) {
    if (!goalId) return null;
    return this.goalRowTargets.find((row) => row.dataset.goalKey === goalId);
  }

  // A goal the therapist adds to the session or jumps to shows even when the
  // note's Customize Goals Shown choice hides it: they asked for it, and the
  // choice decides what the note shows, never what can be recorded. The row
  // asks goals-v3-presentation (on this same section) to show it, which keeps
  // it and its long-term goal on screen until the choice is next changed; the
  // choice the note saves stays as it was.
  _reveal(row) {
    if (!row.classList.contains('gv3-presentation-hidden')) return;
    this.dispatch('reveal', { target: row, prefix: 'goals-v3-presentation' });
  }

  // A row "has data" when any entry value field holds something — the total
  // box (pre-filled 10) and the prompting pick don't count as data on
  // their own.
  _rowHasData(row) {
    return Array.from(row.querySelectorAll('input[name*="[entries]"], textarea[name*="[entries]"]')).some((el) => {
      const name = el.getAttribute('name') || '';
      if (name.endsWith('[total]') || name.endsWith('[prompting_level_id]')) return false;
      return el.value != null && String(el.value).trim() !== '';
    });
  }

  _syncEmptyHint() {
    if (!this.hasEmptyHintTarget) return;
    const anyLanded = this.goalRowTargets.some((row) => row.dataset.landed === 'true');
    this.emptyHintTarget.hidden = anyLanded;
  }

  _landedRows() {
    return this.goalRowTargets.filter((row) => row.dataset.landed === 'true');
  }

  // Stamp every landed row's hidden position input with its DOM order, so
  // the reorder persists through the datum and the server re-renders rows
  // in the therapist's order.
  _renumberPositions() {
    this._landedRows().forEach((row, index) => {
      const input = row.querySelector('input[data-goals-v3-capture-position]');
      if (input) input.value = String(index);
    });
  }

  _clearDropEdges() {
    this.goalRowTargets.forEach((row) => row.classList.remove('gv3-drop-above', 'gv3-drop-below'));
  }

  // Open or tuck away one row's entry band, keeping the caret and the
  // disclosure button's aria-expanded honest. An opened row sizes its session
  // comment: the page sized it as it loaded, and a goal not yet in the session
  // (or hidden with Customize Goals Shown) was out of sight then.
  _setRowOpen(row, open) {
    const detail = row.querySelector('.goals-v3-goal-collapsible');
    if (detail) detail.classList.toggle('hidden', !open);
    const header = row.querySelector('.gv3-expand-row');
    if (header) header.classList.toggle('is-open', open);
    const disclosure = row.querySelector('.gv3-row-disclosure');
    if (disclosure) disclosure.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) fitAutoresizeTextareas(row);
    this._syncToggleAffordance();
  }

  // "Toggle goals" reads as what it will DO next: collapse while everything
  // is open, open while anything is tucked away.
  _syncToggleAffordance() {
    if (!this.hasToggleButtonTarget) return;
    const anyClosed = this._landedRows().some((row) => {
      const detail = row.querySelector('.goals-v3-goal-collapsible');
      return detail && detail.classList.contains('hidden');
    });
    this.toggleButtonTarget.title = anyClosed
      ? 'Open every goal in this session'
      : 'Collapse every goal in this session';
    const icon = this.toggleButtonTarget.querySelector('.bi');
    if (icon) {
      icon.classList.toggle('bi-arrows-expand', anyClosed);
      icon.classList.toggle('bi-arrows-collapse', !anyClosed);
    }
  }

  // The label row's buttons and the Add-goal modal options follow the landed
  // set: Add goal shows while something is still addable, Toggle/Clear show
  // while something is landed, Customize Goals Shown too (its picker lists
  // the goals in the session, so with none it opened on an empty list), and
  // each modal option shows while its goal is NOT in the session.
  _syncSessionControls() {
    const landedCount = this._landedRows().length;
    const total = this.goalRowTargets.length;
    if (this.hasAddButtonTarget) this.addButtonTarget.hidden = landedCount >= total;
    if (this.hasToggleButtonTarget) this.toggleButtonTarget.hidden = landedCount === 0;
    if (this.hasClearButtonTarget) this.clearButtonTarget.hidden = landedCount === 0;
    if (this.hasPresentationButtonTarget) this.presentationButtonTarget.hidden = landedCount === 0;
    // The page-wide panel masters wait for a goal in the session, like the picker.
    if (this.hasPanelMastersTarget) this.panelMastersTarget.hidden = landedCount === 0;

    const landedIds = new Set(this._landedRows().map((row) => row.dataset.goalKey));
    this.addOptionTargets.forEach((option) => {
      option.hidden = landedIds.has(option.dataset.goalId);
    });
    this.addGroupTargets.forEach((group) => {
      const options = group.querySelectorAll('[data-goals-v3-capture-target~="addOption"]');
      group.hidden = Array.from(options).every((option) => option.hidden);
    });
    if (this.hasAddAllLandedTarget) {
      this.addAllLandedTarget.hidden = this.addOptionTargets.some((option) => !option.hidden);
    }
    if (this.hasAddAllTarget) this.addAllTarget.hidden = landedCount >= total;
  }

  _flash(el) {
    el.classList.remove('gv3-landing-flash');
    el.getBoundingClientRect(); // force reflow so a re-flash restarts the animation
    el.classList.add('gv3-landing-flash');
    setTimeout(() => el.classList.remove('gv3-landing-flash'), 1300);
  }

  addEntry(event) {
    event.preventDefault();
    const goalKey = event.params.key;
    if (!goalKey) return;

    const goalRow = this.goalRowTargets.find((row) => row.dataset.goalKey === goalKey);
    if (goalRow) this._newLine(goalRow);
  }

  // A fresh data line for the row from its template, under a key the page
  // makes, or null when the row has no template.
  _newLine(row) {
    const template = row.querySelector('template[data-goals-v3-capture-template]');
    const entries = row.querySelector('[data-goals-v3-capture-target="entries"]');
    if (!template || !entries) return null;

    const entryKey = `new-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    entries.insertAdjacentHTML('beforeend', template.innerHTML.replaceAll('__ENTRY__', entryKey));
    return entries.lastElementChild;
  }

  // --- AI Scribe -----------------------------------------------------------
  // The scribe records session data through the controller rather than into
  // the inputs: a goal has to land before its row submits, a second data point
  // needs a line only the template can make, and the grade and prompting picks
  // are Impulse selects. ai_scribe_controller.js owns which lines and boxes are
  // the AI's (its marks and ownership); this side only knows the rows.

  // What the session records now, per goal id, in the shape the scribe answers
  // in: every landed goal's value-bearing lines and its comment. `keep` says
  // which lines and boxes count (the scribe leaves its own earlier ones out,
  // since it replaces them on the next pass).
  scribeState(keep = () => true) {
    const state = {};
    this._landedRows().forEach((row) => {
      const points = Array.from(row.querySelectorAll('[data-goals-v3-capture-entry]'))
        .filter((line) => keep(line))
        .map((line) => this._linePoint(line))
        .filter(Boolean);
      const box = this._commentBox(row);
      const recorded = {};
      if (points.length) recorded.data_points = points;
      if (box && keep(box) && box.value.trim() !== '') recorded.comment = box.value.trim();
      if (Object.keys(recorded).length) state[row.dataset.goalKey] = recorded;
    });
    return state;
  }

  // Records the scribe's answer ({ goalId: { data_points: [...], comment } }):
  // lands each goal it names, puts each data point on a blank line (the one the
  // page draws, else a new one) and the comment in the comment box when that is
  // empty or `owned` (the scribe's own earlier text). The lines in `replace`
  // go first: the scribe's earlier lines, re-derived by this pass. Each line it
  // writes is stamped as the AI's with `confidence` (D22), which the note keeps
  // on the row unless the therapist edits the line. Returns the lines and boxes
  // it wrote, so the scribe can mark and own them, and the ids of the goals the
  // answer named that have no row on this page (the page shows another
  // discipline's goals since the session was opened), so the scribe can say so
  // rather than drop them; a line it made is flagged data-ai-scribe-added, so
  // Start over can take it back out.
  scribeApply(goals, { replace = [], owned = () => false, confidence = null } = {}) {
    const written = { lines: [], boxes: [], unplaced: [] };
    replace.forEach((line) => line.remove());
    Object.entries(goals || {}).forEach(([goalId, answer]) => {
      if (!answer || typeof answer !== 'object') return;
      const row = this._rowFor(goalId);
      if (!row) {
        written.unplaced.push(goalId);
        return;
      }
      const points = Array.isArray(answer.data_points) ? answer.data_points : [];
      const comment = typeof answer.comment === 'string' ? answer.comment.trim() : '';
      if (!points.length && !comment) return;

      if (row.dataset.landed !== 'true') this.setLanded(goalId, true);
      points.forEach((point) => {
        let line = this._blankLine(row);
        if (!line) {
          line = this._newLine(row);
          if (line) line.dataset.aiScribeAdded = 'true';
        }
        if (line && this._fillLine(line, point)) {
          this._stampLine(line, confidence);
          written.lines.push(line);
        }
      });
      const box = this._commentBox(row);
      if (comment && box && (box.value.trim() === '' || owned(box)) && box.value !== comment) {
        box.value = comment;
        box.dispatchEvent(new Event('input', { bubbles: true }));
        written.boxes.push(box);
      }
    });
    if (written.lines.length || written.boxes.length) this.broadcastState();
    return written;
  }

  _commentBox(row) {
    return row.querySelector('textarea[name$="[new_comment]"]');
  }

  // Marks a line as the AI's: its source and confidence inputs carry what the
  // row will keep (SessionDataReconciler#stamp_source) and its mark shows.
  _stampLine(line, confidence) {
    const source = line.querySelector('input[data-goals-v3-capture-source]');
    const score = line.querySelector('input[data-goals-v3-capture-confidence]');
    const mark = line.querySelector('[data-goals-v3-capture-ai-mark]');
    if (!source) return;
    source.value = 'ai_scribe';
    if (score) score.value = typeof confidence === 'number' ? String(confidence) : '';
    if (mark) {
      mark.hidden = false;
      const words = typeof confidence === 'number' ? `, ${Math.round(confidence * 100)}% confident` : '';
      mark.title = `Entered by the AI from what was said or written${words}. Edit the line to make it yours.`;
    }
  }

  // The mark follows the source input, which a restored copy writes without the stamp.
  _syncMark(line) {
    const source = line.querySelector('input[data-goals-v3-capture-source]');
    const mark = line.querySelector('[data-goals-v3-capture-ai-mark]');
    if (source && mark) mark.hidden = source.value !== 'ai_scribe';
  }

  // A person typing into a line the AI wrote makes it theirs: the source and
  // confidence clear so the row reads as the therapist's, and the mark goes.
  // The AI's own writes dispatch input events too, so the stamp follows them
  // (scribeApply fills the line, then stamps it).
  _claimLine(target) {
    const line = target && target.closest && target.closest('[data-goals-v3-capture-entry]');
    if (!line) return;
    const source = line.querySelector('input[data-goals-v3-capture-source]');
    if (!source || source.value === '') return;
    source.value = '';
    const score = line.querySelector('input[data-goals-v3-capture-confidence]');
    if (score) score.value = '';
    const mark = line.querySelector('[data-goals-v3-capture-ai-mark]');
    if (mark) mark.hidden = true;
  }

  // The row's first line holding no value: the empty line the page draws, or
  // one the therapist emptied. The trials total's prefill and a prompting pick
  // alone do not make a line hold a value, as the save reads it.
  _blankLine(row) {
    return Array.from(row.querySelectorAll('[data-goals-v3-capture-entry]')).find((line) => !this._linePoint(line));
  }

  // A line's data point as the scribe answers it ({ correct, total, prompting }
  // and so on, by the inputs' own keys), or null when the line holds no value.
  _linePoint(line) {
    const point = {};
    let bearing = false;
    line.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.type === 'hidden' && el.dataset.behavior !== 'hidden-field') return;
      const key = this._keyOf(el);
      if (!key) return;
      const value = (el.value || '').trim();
      if (value === '') return;
      if (key === 'prompting_level_id') {
        point.prompting = value;
        return;
      }
      point[key] = value;
      if (key !== 'total') bearing = true;
    });
    return bearing ? point : null;
  }

  // Writes one data point into a line: numbers into their boxes, a grade or a
  // prompting level through its Impulse select (only a value on the list is
  // ever picked). True when anything was written.
  _fillLine(line, point) {
    if (!point || typeof point !== 'object') return false;
    let wrote = false;
    Object.entries(point).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      const name = key === 'prompting' ? 'prompting_level_id' : key;
      const inputs = Array.from(line.querySelectorAll('input, select, textarea'));
      const el = inputs.find((candidate) => this._keyOf(candidate) === name);
      if (!el) return;
      if (el.dataset.behavior === 'hidden-field') {
        if (this._pickOption(el, String(value))) wrote = true;
      } else if (el.value !== String(value)) {
        el.value = String(value);
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        wrote = true;
      }
    });
    if (wrote) {
      this._refreshAccuracy(line);
      recheckRangePairs(this.application, line);
    }
    return wrote;
  }

  // Picks an option on an Impulse select by its value: the submitted hidden
  // field and the visible text move together, like a click on the option.
  _pickOption(hiddenEl, value) {
    const awc = hiddenEl.closest('awc-autocomplete');
    const option = awc && awc.querySelector(`[role="option"][value="${CSS.escape(value)}"]`);
    if (!option || hiddenEl.value === value) return false;
    const text = (option.getAttribute('data-text') || option.textContent || '').trim();
    hiddenEl.value = value;
    hiddenEl.dataset.text = text;
    const visible = awc.querySelector('.awc-autocomplete-input');
    if (visible) visible.value = text;
    hiddenEl.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  // The last bracketed segment of an input's name: "[entries][k][correct]" -> correct.
  _keyOf(el) {
    const match = (el.name || '').match(/\[([^\]]+)\]$/);
    return match ? match[1] : null;
  }

  removeEntry(event) {
    event.preventDefault();
    const entry = event.currentTarget.closest('[data-goals-v3-capture-entry]');
    if (entry) entry.remove();
  }

  // Fired from either half of the trials pair.
  updateAccuracy(event) {
    const entry = event.currentTarget.closest('[data-goals-v3-capture-entry]');
    if (entry) this._refreshAccuracy(entry);
  }

  // The "= 80%" badge the way the prototype's TrialsInput reads it, from
  // whatever the two boxes hold: hidden while the pair is incomplete, and a
  // warning, with the correct box refusing to submit, when correct is more
  // than the total (the note would not save that line).
  _refreshAccuracy(entry) {
    const badge = entry.querySelector('[data-goals-v3-capture-accuracy]');
    const correctInput = entry.querySelector('input[name$="[correct]"]');
    if (!badge || !correctInput) return;

    const totalInput = entry.querySelector('input[name$="[total]"]');
    const correct = correctInput.value !== '' ? Number(correctInput.value) : null;
    const total = totalInput ? Number(totalInput.value) : 0;
    const complete = correct !== null && !Number.isNaN(correct) && total > 0;
    const over = complete && correct > total;

    correctInput.setCustomValidity(over ? 'Correct cannot be more than the total trials.' : '');
    badge.classList.toggle('badge-light', !over);
    badge.classList.toggle('badge-danger', over);
    badge.hidden = !complete;
    if (!complete) {
      badge.textContent = '';
    } else if (over) {
      badge.textContent = 'more than the total';
    } else {
      badge.textContent = `= ${Math.round((correct / total) * 100)}%`;
    }
  }
}
